import { getMobileStack } from '../../perks-nav/mobile-stack';
import { commentCarouselSlot } from '../perks-post-info/mobile-carousel-slots';

const INPUT_SELECTOR = 'textarea[name="commentInput"]';
const MOBILE_BAR_INPUT_SELECTOR = `.single-mobile-comment-bar ${INPUT_SELECTOR}`;
const COMMENTS_CARD_SELECTOR = '.single-post-comments-wrapper';
const KEYBOARD_PROXY_ID = 'single-comment-focus-proxy';
// Matches the #single-mobile-stack breakpoint — the only layout where the
// keyboard handoff below matters.
const MOBILE_STACK_QUERY = '(max-width: 768px)';
const SCROLL_GAP_PX = 16;
const PENDING_TTL_MS = 10_000;
// How long the post page gets to render its comments before a pending
// scroll/focus — and the keyboard proxy holding the keyboard for it — is dropped.
const RENDER_WAIT_TTL_MS = 6_000;
const RENDER_WAIT_INTERVAL_MS = 100;

let pendingScrollUntil = 0;
let pendingFocusUntil = 0;

const isRendered = (el: Element): boolean => el.getClientRects().length > 0;

const findRendered = (selector: string): HTMLTextAreaElement | null =>
  Array.from(document.querySelectorAll<HTMLTextAreaElement>(selector)).find(isRendered) ?? null;

const removeKeyboardProxy = (): void => {
  document.getElementById(KEYBOARD_PROXY_ID)?.remove();
};

// ── Scrolling ───────────────────────────────────────────────────────────────

/** Bottom edge of the theme's fixed/sticky header; 0 when the header scrolls away. */
const getFixedHeaderBottom = (): number => {
  for (const el of document.elementsFromPoint(window.innerWidth / 2, 1)) {
    if (el === document.body || el === document.documentElement) break;
    const { position } = window.getComputedStyle(el);
    if (position !== 'fixed' && position !== 'sticky') continue;
    const { bottom } = el.getBoundingClientRect();
    if (bottom > 0 && bottom < window.innerHeight / 2) return bottom;
  }
  return 0;
};

/** Height the fixed mobile stack (nav, comment bar, player) covers at the bottom. */
const getMobileStackHeight = (): number => {
  const stack = getMobileStack();
  if (!stack || !isRendered(stack)) return 0;
  return Math.max(0, window.innerHeight - stack.getBoundingClientRect().top);
};

/**
 * The page scrollY that puts `el` just below the sticky header ('start') or centred in the
 * space between the header and the mobile stack ('center'), measured against the layout as
 * it is right now.
 */
export const scrollTargetFor = (el: Element, align: 'start' | 'center'): number => {
  const rect = el.getBoundingClientRect();
  const topInset = getFixedHeaderBottom() + SCROLL_GAP_PX;
  const bottomInset = getMobileStackHeight() + SCROLL_GAP_PX;
  const available = window.innerHeight - topInset - bottomInset;
  const centring = align === 'center' ? Math.max(0, (available - rect.height) / 2) : 0;
  return Math.max(0, window.scrollY + rect.top - topInset - centring);
};

/** Smooth-scrolls the page to scrollTargetFor(el, align). */
export const scrollToElement = (el: Element, align: 'start' | 'center'): void => {
  window.scrollTo({ top: scrollTargetFor(el, align), behavior: 'smooth' });
};

const HIGHLIGHT_CLASS = 'single-comment-highlight';
const HIGHLIGHT_MS = 2_400;

/** Flashes a comment's background so the eye lands on it after a scroll — see the matching keyframes. */
const highlightComment = (el: Element): void => {
  el.classList.remove(HIGHLIGHT_CLASS);
  // restart the animation if the same comment is highlighted twice in a row
  void (el as HTMLElement).offsetWidth;
  el.classList.add(HIGHLIGHT_CLASS);
  window.setTimeout(() => el.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS);
};

/**
 * Scrolls to a comment once it has rendered — call after postComment resolves, or when
 * arriving from a notification email's deep link (with `highlight`, since the reader did
 * not just write it and needs showing which one it is).
 */
export const scrollToComment = (commentId: string, { highlight = false } = {}): void => {
  let attempts = 0;
  const attempt = () => {
    const el = document.querySelector(`[data-comment-id="${CSS.escape(commentId)}"]`);
    if (el) {
      scrollToElement(el, 'center');
      if (highlight) highlightComment(el);
    } else if (attempts++ < 5) {
      window.setTimeout(attempt, RENDER_WAIT_INTERVAL_MS);
    }
  };
  // Give the store update a beat to render, and the keyboard one to close.
  window.setTimeout(attempt, RENDER_WAIT_INTERVAL_MS);
};

// ── Focusing ────────────────────────────────────────────────────────────────

/**
 * Slides the post page's mobile carousel back to the comment bar when the mini
 * player slide is showing, so the bar is what the keyboard opens onto.
 */
export const showCommentSlide = (): void => {
  const track = commentCarouselSlot.value?.parentElement;
  if (track && track.scrollLeft > 0) {
    track.scrollTo({ left: 0, behavior: 'smooth' });
  }
};

/**
 * Focuses the fixed mobile comment bar's textarea, sliding the carousel back to
 * it first. Returns false when the bar isn't rendered (desktop widths).
 * Call it synchronously from the tap handler: iOS only raises the keyboard for
 * a focus() made while a user gesture is being processed.
 */
export const focusMobileCommentInput = (): boolean => {
  const input = findRendered(MOBILE_BAR_INPUT_SELECTOR);
  if (!input) return false;
  showCommentSlide();
  // The bar is fixed, and letting focus() scroll the carousel track would cut
  // the smooth slide short.
  input.focus({ preventScroll: true });
  removeKeyboardProxy();
  return true;
};

/**
 * Focuses whichever comment textarea is rendered at the current width: the
 * mobile bar on phones, otherwise the form at the top of the comments card
 * (scrolled into view unless the caller is already scrolling the page).
 */
export const focusCommentInput = ({ scroll = true } = {}): boolean => {
  if (focusMobileCommentInput()) return true;
  const input = findRendered(INPUT_SELECTOR);
  if (!input) return false;
  if (scroll) scrollToElement(input, 'center');
  input.focus({ preventScroll: true });
  removeKeyboardProxy();
  return true;
};

// ── Feed → post handoff ─────────────────────────────────────────────────────

/** Arms a one-shot scroll to the comments card on the next post page. */
export const requestCommentsScroll = (): void => {
  pendingScrollUntil = Date.now() + PENDING_TTL_MS;
};

/**
 * Arms a one-shot focus of the comment input on the next post page. On phones a
 * throwaway textarea takes focus during the tap so the keyboard is already up
 * when the real input mounts: moving focus between fields keeps it open,
 * whereas a bare focus() after the async navigation and comment load is
 * ignored by iOS.
 */
export const requestCommentInputFocus = (label: string): void => {
  pendingFocusUntil = Date.now() + PENDING_TTL_MS;
  if (!window.matchMedia(MOBILE_STACK_QUERY).matches) return;
  if (document.getElementById(KEYBOARD_PROXY_ID)) return;

  const proxy = document.createElement('textarea');
  proxy.id = KEYBOARD_PROXY_ID;
  proxy.setAttribute('aria-label', label);
  proxy.tabIndex = -1;
  // 16px font: below that iOS zooms the page on focus.
  proxy.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;' +
    'font-size:16px;resize:none;pointer-events:none;';
  document.body.appendChild(proxy);
  proxy.focus({ preventScroll: true });
  // Nothing took the handoff (comments disabled, post failed to load): drop the
  // proxy so the keyboard closes instead of sitting over the page.
  window.setTimeout(removeKeyboardProxy, RENDER_WAIT_TTL_MS);
};

/** Reads and clears both requests — the post page calls this once its layout is final. */
export const consumePendingCommentEntry = (): { scroll: boolean; focus: boolean } => {
  const now = Date.now();
  const entry = { scroll: now < pendingScrollUntil, focus: now < pendingFocusUntil };
  pendingScrollUntil = 0;
  pendingFocusUntil = 0;
  return entry;
};

/**
 * Carries out a consumed entry once the comments card inside `container` has
 * rendered — it only appears after the comments load, and the textarea with it.
 */
export const arriveAtComments = (
  container: Element,
  { scroll, focus }: { scroll: boolean; focus: boolean }
): void => {
  const deadline = Date.now() + RENDER_WAIT_TTL_MS;
  const attempt = () => {
    const card = container.querySelector(COMMENTS_CARD_SELECTOR);
    if (card) {
      if (scroll) scrollToElement(card, 'start');
      if (focus) focusCommentInput({ scroll: !scroll });
    } else if (Date.now() < deadline) {
      window.setTimeout(attempt, RENDER_WAIT_INTERVAL_MS);
    } else {
      removeKeyboardProxy();
    }
  };
  attempt();
};
